import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  getPreferredSingleStoreId,
  getSingleStoreSelectionFromSearchParams,
  hasExplicitSingleStoreParams,
  normalizeMultiSelection,
  normalizeSingleStoreSelection,
  readMultiSelectionFromSession,
  readSingleStoreSelectionFromSession,
  STORE_INSIGHTS_SHARED_MULTI_SELECTION_SESSION_KEY,
  type StoreInsightsSingleSelectionState,
  writeSharedMultiSelectionFromSingleStore,
  writeSingleStoreSelectionToSession,
} from "@/pages/store-insights/shared/storeInsightsSelectionSession";
import { useStoreInsightsLookups } from "@/pages/store-insights/shared/useStoreInsightsLookups";
import { type Store } from "@/services/features/StoreService";

interface UseStoreInsightsSingleStoreSelectionOptions {
  sessionKey: string;
  fallbackMultiSelectionSessionKeys?: string[];
}

interface FallbackMultiSelectionResolution {
  firstStoreId: number | null;
}

export const useStoreInsightsSingleStoreSelection = ({
  sessionKey,
  fallbackMultiSelectionSessionKeys = [STORE_INSIGHTS_SHARED_MULTI_SELECTION_SESSION_KEY],
}: UseStoreInsightsSingleStoreSelectionOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores: visibleStores, loading } = useStoreInsightsLookups();
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const lastInternalSearchRef = useRef<string | null>(null);
  const hasInitializedSelectionRef = useRef(false);
  const currentSearch = searchParams.toString();
  const hasPendingInternalSearchUpdate = lastInternalSearchRef.current !== null;

  const getFallbackMultiSelection = useCallback((): FallbackMultiSelectionResolution | null => {
    for (const fallbackSessionKey of fallbackMultiSelectionSessionKeys) {
      const fallbackSelection = readMultiSelectionFromSession(fallbackSessionKey);
      if (!fallbackSelection) {
        continue;
      }

      const requestedStoreId = getPreferredSingleStoreId(fallbackSelection.storeIds, visibleStores);
      if (requestedStoreId !== null) {
        return {
          firstStoreId: requestedStoreId,
        };
      }

      const normalizedFallbackSelection = normalizeMultiSelection(
        fallbackSelection,
        visibleStores,
        [],
        true,
      );
      if (normalizedFallbackSelection.storeIds.length) {
        return {
          firstStoreId: normalizedFallbackSelection.storeIds[0] ?? null,
        };
      }
    }

    return null;
  }, [fallbackMultiSelectionSessionKeys, visibleStores]);

  const updateSearchParams = useCallback(
    (storeId: number | null) => {
      const params = new URLSearchParams(searchParams);

      if (storeId !== null) {
        params.set("storeId", String(storeId));
      } else {
        params.delete("storeId");
      }
      params.delete("storeIds");

      const nextSearch = params.toString();
      if (nextSearch === searchParams.toString()) {
        return;
      }

      lastInternalSearchRef.current = nextSearch;
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!visibleStores.length) {
      if (selectedStore !== null) {
        setSelectedStore(null);
      }
      updateSearchParams(null);
      writeSingleStoreSelectionToSession(sessionKey, { storeId: null });
      return;
    }

    const sessionSelection = readSingleStoreSelectionFromSession(sessionKey);
    const currentSelection: StoreInsightsSingleSelectionState = {
      storeId: selectedStore?.id ?? null,
      hasUserSelectedStore: sessionSelection?.hasUserSelectedStore,
    };
    const sourceSelection = hasPendingInternalSearchUpdate
      ? currentSelection
      : hasExplicitSingleStoreParams(searchParams)
        ? {
            ...getSingleStoreSelectionFromSearchParams(searchParams, visibleStores),
            hasUserSelectedStore: true,
          }
        : (sessionSelection ?? { storeId: null });
    let normalizedSelection = normalizeSingleStoreSelection(sourceSelection, visibleStores);

    if (
      !hasPendingInternalSearchUpdate &&
      !hasExplicitSingleStoreParams(searchParams) &&
      !hasInitializedSelectionRef.current
    ) {
      const fallbackMultiSelection = getFallbackMultiSelection();
      if (
        !sessionSelection?.hasUserSelectedStore &&
        fallbackMultiSelection &&
        fallbackMultiSelection.firstStoreId !== null
      ) {
        normalizedSelection = {
          storeId: fallbackMultiSelection.firstStoreId,
          hasUserSelectedStore: false,
        };
      }
    }

    const nextSelectedStore =
      normalizedSelection.storeId !== null
        ? (visibleStores.find((store) => store.id === normalizedSelection.storeId) ?? null)
        : null;

    if (nextSelectedStore?.id !== selectedStore?.id) {
      setSelectedStore(nextSelectedStore);
    }

    updateSearchParams(normalizedSelection.storeId);
    writeSingleStoreSelectionToSession(sessionKey, normalizedSelection);
    if (!hasPendingInternalSearchUpdate && hasExplicitSingleStoreParams(searchParams)) {
      writeSharedMultiSelectionFromSingleStore(nextSelectedStore);
    }
    hasInitializedSelectionRef.current = true;
  }, [
    getFallbackMultiSelection,
    hasPendingInternalSearchUpdate,
    loading,
    searchParams,
    selectedStore,
    selectedStore?.id,
    sessionKey,
    updateSearchParams,
    visibleStores,
  ]);

  useEffect(() => {
    if (lastInternalSearchRef.current === currentSearch) {
      lastInternalSearchRef.current = null;
    }
  }, [currentSearch]);

  const onStoreChange = useCallback(
    (store: Store | null) => {
      setSelectedStore(store);
      updateSearchParams(store?.id ?? null);
      writeSingleStoreSelectionToSession(sessionKey, {
        storeId: store?.id ?? null,
        hasUserSelectedStore: store !== null,
      });
      writeSharedMultiSelectionFromSingleStore(store);
    },
    [sessionKey, updateSearchParams],
  );

  return {
    stores: visibleStores,
    filteredStores: visibleStores,
    selectedStore,
    loading,
    onStoreChange,
  };
};
